/**
 * Apply an affine (or a preset) to a whole RGBA buffer.
 *
 * The loop is deliberately flat: no allocation inside, no bounds checks beyond
 * what the engine does, and a tight `for` over 4-byte pixels. This is the hot
 * path on the CPU fallback, and the reference the GPU path is checked against.
 *
 * Alpha is passed through untouched.
 *
 * @module core/apply
 */

import type { Affine, Mat3, Raster, StretchParams, Vec3 } from './types.ts';
import { clamp255 } from './stretch.ts';
import { collapse } from './compose.ts';
import { buildLutTables, applyLutPixel } from './luts.ts';

/** Write `clamp(M x + b)` for every pixel of `raster`, in place. */
export function applyAffineInPlace(raster: Raster, affine: Affine): Raster {
  const { data } = raster;
  const { M, b } = affine;
  const m0 = M[0]!;
  const m1 = M[1]!;
  const m2 = M[2]!;
  const m3 = M[3]!;
  const m4 = M[4]!;
  const m5 = M[5]!;
  const m6 = M[6]!;
  const m7 = M[7]!;
  const m8 = M[8]!;
  const b0 = b[0]!;
  const b1 = b[1]!;
  const b2 = b[2]!;

  for (let px = 0; px < data.length; px += 4) {
    const r = data[px] as number;
    const g = data[px + 1] as number;
    const bl = data[px + 2] as number;
    // Column-major: out[row] = sum_col M[col*3+row] * v[col].
    const o0 = m0 * r + m3 * g + m6 * bl + b0;
    const o1 = m1 * r + m4 * g + m7 * bl + b1;
    const o2 = m2 * r + m5 * g + m8 * bl + b2;
    data[px] = clamp255(o0);
    data[px + 1] = clamp255(o1);
    data[px + 2] = clamp255(o2);
  }
  return raster;
}

/** Apply a preset to an RGBA buffer, in place. Collapses once, then loops. */
export function applyStretchInPlace(raster: Raster, params: StretchParams): Raster {
  return applyAffineInPlace(raster, collapse(params));
}

/**
 * Apply an affine through the nine axis tables of hypothesis T3.
 *
 * For each output channel `c` there are three tables:
 *
 *     T_c[col][v] = M[col*3+c] * v
 *
 * and the output is `T_c0[R] + T_c1[G] + T_c2[B] + b[c]`. Nine lookups and nine
 * adds per pixel, instead of nine multiplies and nine adds. The accumulation
 * order is identical to `applyAffineInPlace`, so the two are bit-for-bit equal
 * — see `luts.test.ts`.
 *
 * The tables are `Float64Array`s here. On hardware without an FPU the point of
 * the trick is to store integers instead; that changes nothing about the
 * structure, only the table element type.
 */
export function applyLutInPlace(raster: Raster, affine: Affine): Raster {
  const tables = buildLutTables(affine);
  const b0 = affine.b[0]!;
  const b1 = affine.b[1]!;
  const b2 = affine.b[2]!;
  const { data } = raster;
  for (let px = 0; px < data.length; px += 4) {
    const r = data[px] as number;
    const g = data[px + 1] as number;
    const bl = data[px + 2] as number;
    data[px] = clamp255(tables[0]![r]! + tables[1]![g]! + tables[2]![bl]! + b0);
    data[px + 1] = clamp255(tables[3]![r]! + tables[4]![g]! + tables[5]![bl]! + b1);
    data[px + 2] = clamp255(tables[6]![r]! + tables[7]![g]! + tables[8]![bl]! + b2);
  }
  return raster;
}

/** Allocate a new raster and fill it from `source`, leaving `source` alone. */
export function mapAffine(source: Raster, affine: Affine): Raster {
  const data = new Uint8ClampedArray(source.data.length);
  data.set(source.data as unknown as ArrayLike<number>);
  const out: Raster = { data, width: source.width, height: source.height };
  applyAffineInPlace(out, affine);
  return out;
}

/** One pixel through an affine, clamped. Small helper for tests and previews. */
export function applyPixel(affine: Affine, x: ArrayLike<number>): Vec3 {
  const { M, b } = affine;
  return Float64Array.of(
    clamp255(M[0]! * x[0]! + M[3]! * x[1]! + M[6]! * x[2]! + b[0]!),
    clamp255(M[1]! * x[0]! + M[4]! * x[1]! + M[7]! * x[2]! + b[1]!),
    clamp255(M[2]! * x[0]! + M[5]! * x[1]! + M[8]! * x[2]! + b[2]!),
  );
}

/** True when every pixel of the result equals the reference implementation. */
export function lutMatchesAffine(raster: Raster, affine: Affine): boolean {
  const a = new Uint8ClampedArray(raster.data.length);
  a.set(raster.data as unknown as ArrayLike<number>);
  const b = new Uint8ClampedArray(raster.data.length);
  b.set(raster.data as unknown as ArrayLike<number>);
  applyAffineInPlace({ data: a, width: raster.width, height: raster.height }, affine);
  applyLutInPlace({ data: b, width: raster.width, height: raster.height }, affine);
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

/** Expose the per-channel table index so callers do not re-derive it. */
export function lutChannelTables(affine: Affine): Float64Array[] {
  return buildLutTables(affine);
}

export { applyLutPixel };
export type { Mat3 };
